import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { baseUrl } from 'utils/axios'
import { IRole, IStateRoles } from 'types/roles.interface'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { INameId } from 'constants/globalTypes'
import {
  IClientRolesPatchRequest,
  IClientRolesPostRequest,
  IPermissionPolicy,
  ISingleClientRoleGetResponse,
} from 'types/api/clientRoles.interface'

const initialState: IStateRoles = {
  roles: [],
  isLoading: false,
  singleRole: {
    permissionPolicy: null,
    name: '',
    machineModels: [],
    operations: [],
    permissions: null,
    logisticsInfo: null,
    freeDays: [],
    workScheduleModelId: null,
    shifts: [],
    shiftChangeType: null,
    shiftChangeInterval: 0,
    vacationDays: 0,
  },
  totalCount: 0,
  totalPages: 0,
  permissionPolicies: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  singlePermissionPolicy: null,
}

export const getClientRoles = createAsyncThunk(
  'clientRoles/getRoles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<INameId>> = await baseUrl.get('roles', {
        params,
      })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteRolePermissionPolicy = createAsyncThunk(
  'clientRoles/deleteRolePermissionPolicy',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('permission-policy/role/' + id)
      await thunkAPI.dispatch(getRolePermissionPolicies(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getRolePermissionPolicies = createAsyncThunk(
  'clientRoles/getRolePermissionPolicies',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IPermissionPolicy>> = await baseUrl.get(
        'permission-policy/role',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleClientRole = createAsyncThunk(
  'clientRoles/getSingleRole',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISingleClientRoleGetResponse> = await baseUrl.get('roles/' + id)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleRolePermissionPolicy = createAsyncThunk(
  'clientRoles/getSingleRolePermissionPolicy',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPermissionPolicy> = await baseUrl.get(
        'permission-policy/role/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addRolePermissionPolicy = createAsyncThunk(
  'clientRoles/addRolePermissionPolicy',
  async (state: Omit<IPermissionPolicy, 'id'>, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISingleClientRoleGetResponse> = await baseUrl.post(
        'permission-policy/role',
        state,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editRolePermissionPolicy = createAsyncThunk(
  'clientRoles/editRolePermissionPolicy',
  async (state: IPermissionPolicy, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISingleClientRoleGetResponse> = await baseUrl.patch(
        'permission-policy/role/' + state.id,
        state,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addClientRole = createAsyncThunk(
  'clientRoles/addRole',
  async (role: IRole, thunkAPI) => {
    try {
      const forSubmit: IClientRolesPostRequest = {
        name: role.name,
        permissionPolicyId: role.permissionPolicy?.value,
        permissions: role.permissions,
        freeDays: role.freeDays,
        workScheduleModelId: role.workScheduleModelId?.value as number,
        shifts: role.shifts,
        shiftChangeType: role.shiftChangeType?.value as number,
        shiftChangeInterval: role.shiftChangeInterval,
        vacationDays: role.vacationDays,
      }
      if (role.logisticsInfo) {
        forSubmit.logisticsInfo = {
          ...role.logisticsInfo,
          // accommodationId: user.logisticsInfo?.accommodation?.value,
          transportationType: role.logisticsInfo?.transportationType?.value,

          extraDutyAllowanceInCompanyAmount:
            role.logisticsInfo?.extraDutyAllowanceInCompanyAmount ?? 0,
          extraDutyAllowanceOutCompanyAmount:
            role.logisticsInfo?.extraDutyAllowanceOutCompanyAmount ?? 0,

          responsibilityAllowanceOutCompanyAmount:
            role.logisticsInfo?.responsibilityAllowanceOutCompanyAmount ?? 0,
          responsibilityAllowanceInCompanyAmount:
            role.logisticsInfo?.responsibilityAllowanceInCompanyAmount ?? 0,

          foodAllowanceInCompanyAmount: role.logisticsInfo?.foodAllowanceInCompanyAmount ?? 0,
          foodAllowanceOutCompanyAmount: role.logisticsInfo?.foodAllowanceOutCompanyAmount ?? 0,

          accomodationAllowanceInCompanyAmount:
            role.logisticsInfo?.accomodationAllowanceInCompanyAmount ?? 0,
          accomodationAllowanceOutCompanyAmount:
            role.logisticsInfo?.accomodationAllowanceOutCompanyAmount ?? 0,

          accomodationTierPolicy: role.logisticsInfo?.accomodationTierPolicy?.value || null,

          transportationAllowanceInCompanyAmount:
            role.logisticsInfo?.transportationAllowanceInCompanyAmount ?? 0,
          transportationAllowanceOutCompanyAmount:
            role.logisticsInfo?.transportationAllowanceOutCompanyAmount ?? 0,
        }
      }
      const resp = await baseUrl.post('/roles', forSubmit, thunkAPI)
      await thunkAPI.dispatch(getClientRoles(getParams({ location })))
      return { id: resp.data.id, name: role.name }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteClientRole = createAsyncThunk(
  'clientRoles/deleteRole',
  async (id: string | number, thunkAPI) => {
    try {
      try {
        await baseUrl.delete('roles/' + id, thunkAPI)
        await thunkAPI.dispatch(getClientRoles(getParams({ location })))
        return id
      } catch (error) {
        return error
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const updateClientRole = createAsyncThunk(
  'clientRoles/updateRole',
  async (role: IRole, thunkAPI) => {
    try {
      const forSubmit: IClientRolesPatchRequest = {
        permissionPolicyId: role.permissionPolicy?.value,
        name: role.name,
        id: role.id,
        permissions: role.permissions,
        freeDays: role.freeDays,
        workScheduleModelId: role.workScheduleModelId?.value as number,
        shifts: role.shifts,
        shiftChangeType: role.shiftChangeType?.value as number,
        shiftChangeInterval: role.shiftChangeInterval,
        vacationDays: role.vacationDays,
      }
      if (role.logisticsInfo) {
        forSubmit.logisticsInfo = {
          ...role.logisticsInfo,
          // accommodationId: user.logisticsInfo?.accommodation?.value,
          transportationType: role.logisticsInfo?.transportationType?.value || 1,

          extraDutyAllowanceInCompanyAmount:
            role.logisticsInfo?.extraDutyAllowanceInCompanyAmount ?? 0,
          extraDutyAllowanceOutCompanyAmount:
            role.logisticsInfo?.extraDutyAllowanceOutCompanyAmount ?? 0,

          responsibilityAllowanceOutCompanyAmount:
            role.logisticsInfo?.responsibilityAllowanceOutCompanyAmount ?? 0,
          responsibilityAllowanceInCompanyAmount:
            role.logisticsInfo?.responsibilityAllowanceInCompanyAmount ?? 0,

          foodAllowanceInCompanyAmount: role.logisticsInfo?.foodAllowanceInCompanyAmount ?? 0,
          foodAllowanceOutCompanyAmount: role.logisticsInfo?.foodAllowanceOutCompanyAmount ?? 0,

          accomodationTierPolicy: role.logisticsInfo?.accomodationTierPolicy?.value || null,

          accomodationAllowanceInCompanyAmount:
            role.logisticsInfo?.accomodationAllowanceInCompanyAmount ?? 0,
          accomodationAllowanceOutCompanyAmount:
            role.logisticsInfo?.accomodationAllowanceOutCompanyAmount ?? 0,

          transportationAllowanceInCompanyAmount:
            role.logisticsInfo?.transportationAllowanceInCompanyAmount ?? 0,
          transportationAllowanceOutCompanyAmount:
            role.logisticsInfo?.transportationAllowanceOutCompanyAmount ?? 0,
        }
      }
      await baseUrl.patch(`roles/${role.id}`, forSubmit, thunkAPI)
      // thunkAPI.dispatch(getRoles())
      return { id: role.id, name: role.name }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const clientRolesSlice = createSlice({
  name: 'clientRoles',
  initialState,
  reducers: {
    clearClientRole: (state) => {
      state.singleRole = {
        name: '',
        permissionPolicy: null,
        machineModels: [],
        operations: [],
        permissions: null,
        logisticsInfo: null,
        freeDays: [],
        workScheduleModelId: null,
        shifts: [],
        shiftChangeType: null,
        shiftChangeInterval: 0,
        vacationDays: 0,
      }
    },
    clearClientRolesList: (state) => {
      state.roles = []
      state.totalCount = null
      state.totalPages = null
    },
    clearRolePermssionPolicyList: (state) => {
      state.permissionPolicies = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearSingleRolePermissionPolicy: (state) => {
      state.singlePermissionPolicy = null
    },
  },
  extraReducers: {
    [getClientRoles.pending.type]: (state) => {
      state.isLoading = true
    },
    [getClientRoles.fulfilled.type]: (
      state: IStateRoles,
      { payload }: { payload: IPaginatedResponse<INameId> },
    ) => {
      state.roles = payload.data
      state.isLoading = false
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
    },
    [getClientRoles.rejected.type]: (state) => {
      state.isLoading = false
    },
    [getRolePermissionPolicies.pending.type]: (state) => {
      state.isLoading = true
    },
    [getRolePermissionPolicies.fulfilled.type]: (
      state: IStateRoles,
      { payload }: { payload: IPaginatedResponse<IPermissionPolicy> },
    ) => {
      state.isLoading = false
      state.permissionPolicies = payload
    },
    [getRolePermissionPolicies.rejected.type]: (state) => {
      state.isLoading = false
    },
    [deleteRolePermissionPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteRolePermissionPolicy.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Permission policy successfully deleted')
    },
    [deleteRolePermissionPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteClientRole.fulfilled.type]: (state: IStateRoles) => {
      state.isLoading = false
      toast.success('Role deleted')
    },
    [addClientRole.pending.type]: (state) => {
      state.isLoading = true
    },
    [addClientRole.fulfilled.type]: () => {
      toast.success('Role added')
    },
    [addClientRole.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleClientRole.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleClientRole.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.singleRole = {
        ...payload,
        permissionPolicy: {
          value: payload.permissionPolicy.id,
          label: payload.permissionPolicy.policyName,
        },
        permissions: {
          dashboard: payload.permissionPolicy.dashboard,
          aimVision: payload.permissionPolicy.aimVision,
          hr: payload.permissionPolicy.hr,
          fuel: payload.permissionPolicy.fuel,
          machinery: payload.permissionPolicy.machinery,
          inventory: payload.permissionPolicy.inventory,
          analytics: payload.permissionPolicy.analytics,
          resources: payload.permissionPolicy.resources,
          contractors: payload.permissionPolicy.contractors,
        },
      }
    },
    [getSingleClientRole.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [updateClientRole.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateClientRole.fulfilled.type]: (state: IStateRoles, { payload }: { payload: INameId }) => {
      state.isLoading = false
      const foundIndex = state.roles.findIndex((item) => item.id === payload.id)
      state.roles[foundIndex] = payload
      toast.success('Role updated')
    },
    [updateClientRole.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [addRolePermissionPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [addRolePermissionPolicy.fulfilled.type]: (state: IStateRoles) => {
      state.isLoading = false
      toast.success('Role permission policy successfully added')
    },
    [addRolePermissionPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleRolePermissionPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleRolePermissionPolicy.fulfilled.type]: (
      state: IStateRoles,
      { payload }: { payload: IPermissionPolicy },
    ) => {
      state.isLoading = false
      state.singlePermissionPolicy = payload
    },
    [getSingleRolePermissionPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editRolePermissionPolicy.pending.type]: (state) => {
      state.isLoading = true
    },
    [editRolePermissionPolicy.fulfilled.type]: (state: IStateRoles) => {
      state.isLoading = false
      toast.success('Role permission policy successfully updated')
    },
    [editRolePermissionPolicy.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearClientRole,
  clearClientRolesList,
  clearRolePermssionPolicyList,
  clearSingleRolePermissionPolicy,
} = clientRolesSlice.actions

export default clientRolesSlice.reducer
